import { HttpClient } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { OKTA_AUTH } from "@okta/okta-angular";
import OktaAuth from "@okta/okta-auth-js";
import { ICurrentUser } from "@models/user-management/user";
import { UserPermissions } from "@models/user-management/user";
import {
  BehaviorSubject,
  distinctUntilChanged,
  map,
  Observable,
  of,
} from "rxjs";
import { HARD_USER } from "@hardcode-kz/ui";
import { BaseService } from "../http/base.service";
import { CalAngularService } from "@cvx/cal-angular";
import { NotificationService } from "../notification/notification.service";
import { CalAuthService } from "./cal-auth.service";
import { OktaAuthService } from "./okta-auth.service";
import { IAuthStrategy, AuthProviderEnum } from "@models/core/auth";
import { AccessToken, RefreshToken } from "@models/http/http-options";

@Injectable({
  providedIn: "root",
})
export class AuthService extends BaseService {
  private strategies: { [key: string]: IAuthStrategy };
  private version = 1;
  public currentUser$ = new BehaviorSubject<ICurrentUser | null>(null);

  private setCurrentUserState(user: ICurrentUser | null): void {
    this.currentUser$.next(user);
    this.hardUserToken$.next(user);
  }

  /**
   * Получить пользователя через стратегию и обновить currentUser$
   */
  public async updateCurrentUser(): Promise<ICurrentUser | null> {
    const strategy = await this.getStrategy();
    let user: ICurrentUser | null = null;
    if (strategy) {
      // Получаем токен и сохраняем в localStorage для HTTP-интерцептора
      await strategy.getToken();
      user = await strategy.getCurrentUser();
    }

    this.setCurrentUserState(user);
    return user;
  }

  public setCurrentUser(user: ICurrentUser | null): void {
    this.setCurrentUserState(user);
  }

  constructor(
    http: HttpClient,
    private readonly calAuthService: CalAuthService,
    private readonly oktaAuthService: OktaAuthService,
    @Inject(HARD_USER)
    private readonly hardUserToken$: BehaviorSubject<ICurrentUser | null>,
    private readonly notificationService: NotificationService,
    private readonly calAngularService: CalAngularService, // still needed for signInWithCal
    @Inject(OKTA_AUTH) private oktaAuth: OktaAuth, // still needed for signInWithOkta
  ) {
    super(http);
    this.strategies = {
      [AuthProviderEnum.AzureAD]: this.calAuthService,
      [AuthProviderEnum.Okta]: this.oktaAuthService,
    };
  }

  async getStrategy(): Promise<IAuthStrategy | undefined> {
    const idp = localStorage.getItem("idp");
    if (idp && this.strategies[idp]) {
      return this.strategies[idp];
    }
    // Fallback: try to detect which auth system is active
    try {
      if (
        this.oktaAuth &&
        typeof this.oktaAuth.isAuthenticated === "function"
      ) {
        const isOktaAuth = await this.oktaAuth.isAuthenticated();
        if (isOktaAuth) {
          localStorage.setItem("idp", AuthProviderEnum.Okta);
          return this.strategies[AuthProviderEnum.Okta];
        }
      }
    } catch {}
    try {
      const isCalAuth = await new Promise<boolean>((resolve) => {
        this.calAngularService.isUserSignedIn().subscribe({
          next: (result) => resolve(result),
          error: () => resolve(false),
        });
      });
      if (isCalAuth) {
        localStorage.setItem("idp", AuthProviderEnum.AzureAD);
        return this.strategies[AuthProviderEnum.AzureAD];
      }
    } catch {}
    return undefined;
  }

  async getCurrentUser(): Promise<ICurrentUser | null> {
    return this.currentUser$.value;
  }

  async isUserSignedIn(): Promise<boolean> {
    const strategy = await this.getStrategy();
    return strategy ? strategy.isUserSignedIn() : false;
  }

  async getTokens(): Promise<string | undefined> {
    const strategy = await this.getStrategy();
    return strategy ? strategy.getToken() : undefined;
  }

  async userInitiatedSignOut(): Promise<void> {
    const strategy = await this.getStrategy();
    return strategy ? strategy.userInitiatedSignOut() : Promise.resolve();
  }

  async getUserRoles(): Promise<string[] | undefined> {
    const strategy = await this.getStrategy();
    return strategy ? strategy.getUserRoles() : undefined;
  }

  anyPermission(permissionCodes: UserPermissions[]): Observable<boolean> {
    if (!permissionCodes?.length) {
      return of(true);
    }

    return this.currentUser$.pipe(
      map((user) =>
        permissionCodes.some((permission) =>
          (user?.permissionCodes || []).includes(permission),
        ),
      ),
      distinctUntilChanged(),
    );
  }

  redirectAccessDenied(): Promise<boolean> {
    return this.router.navigate(["/access-denied"], { replaceUrl: true });
  }

  async logout(): Promise<void> {
    // Сначала выходим через стратегию — getStrategy() ещё читает idp из localStorage
    await this.userInitiatedSignOut();
    // После выхода очищаем состояние
    this.setCurrentUserState(null);
    this.clearToken();
    localStorage.removeItem(AccessToken);
    localStorage.removeItem(RefreshToken);
    localStorage.removeItem("idp");
  }

  /**
   * Запустить авторизацию через Okta (редирект)
   */
  public async signInWithOkta(): Promise<void> {
    if (
      this.oktaAuth &&
      typeof this.oktaAuth.signInWithRedirect === "function"
    ) {
      await this.oktaAuth.signInWithRedirect();
    } else {
      throw new Error("OktaAuth не инициализирован");
    }
  }

  /**
   * Запустить авторизацию через CalAngular
   */
  public signInWithCal() {
    return this.calAngularService.userInitiatedSignIn();
  }
}
